import * as React from 'react';
import type { CampaignNumberPreviewQuery, SalesUser } from '@ooh/contracts';
import type { CampaignNumberPreviewState } from '../../offers/create-fields/campaign-number-preview-field';
import { toSalesUserOptions } from '../../offers/lib/salesperson-gating';
import { useQuickAddCreateDraftForm } from './quick-add-offer-picker-form';
import { useQuickAddCoCreators } from './use-quick-add-co-creators';
import { QuickAddOfferPickerPanel } from './quick-add-offer-picker-panel';
import { QuickAddOfferPickerModal } from './quick-add-offer-picker-modal';
import {
  deriveEffectiveProps,
  useQuickAddOfferPickerState,
} from './use-quick-add-offer-picker-state';

export type { QuickAddCreateDraftFormValues } from './quick-add-offer-picker-form';

const CREATE_FORM_ID = 'quick-add-offer-create-form';

export interface QuickAddOfferDraftOption {
  id: string;
  title: string;
  clientName: string;
  unitCount: number;
  editable: boolean;
  lastUsed?: boolean;
  statusLabel?: string;
  disabledReason?: string;
}

export interface QuickAddClientOption {
  label: string;
  value: string;
  /** The client's brands, when known — powers the "creating"-mode brand select. */
  brands?: string[];
  /** The client's acronym, when known — feeds the campaign-number preview (offer-draft-numbering). */
  acronym?: string;
}

/**
 * A caller-supplied fixed destination offer. When set, the picker is pinned to
 * `summary` mode for this offer only — the draft-switching ("Zmień"), draft-list
 * (`changing`), and create-draft (`creating`) affordances are all suppressed.
 */
export interface QuickAddOfferPickerLockedTarget {
  offerId: string;
  offerName: string;
}

export interface QuickAddOfferPickerProps {
  presentation: 'modal' | 'popover';
  mediaUnitIds?: Array<string | number>;
  title: string;
  subtitle: string;
  drafts: QuickAddOfferDraftOption[];
  selectedDraftId: string | null;
  /** Fixed destination offer from an offer-context caller. See `QuickAddOfferPickerLockedTarget`. */
  lockedTarget?: QuickAddOfferPickerLockedTarget | null;
  /**
   * Prefills the `creating`-mode campaign-duration field (see `campaignDurationLabel`
   * below) — set by calendar-driven callers (CU-86cb8grgr) with the resolved
   * min(dateFrom)-max(dateTo) of the selection so a newly created offer defaults to
   * the same window the caller just picked in the availability calendar. The field
   * stays independently editable regardless of whether this is provided. Omitted by
   * callers with no calendar-selection context (map pin, bulk/row quick-add).
   */
  initialDefaultDateFrom?: Date | null;
  initialDefaultDateTo?: Date | null;
  campaignDurationLabel?: string;
  campaignDurationPlaceholder?: string;
  campaignDurationInvalidRangeLabel?: string;
  clientOptions: QuickAddClientOption[];
  selectedClientValue?: string;
  /**
   * Live campaign-number preview for the "creating" mode form
   * (offer-draft-numbering / "Campaign-number preview on offer creation
   * forms"), computed by the caller via `useCampaignNumberPreview` and
   * passed down as plain data — mirrors `salesUsers`/`authUserId` below:
   * this component must never call `useCampaignNumberPreview` (or any other
   * query/router hook) itself, since `apps/mockups` renders it directly with
   * no such context. Undefined renders no preview field at all.
   */
  campaignNumberPreview?: CampaignNumberPreviewState;
  /**
   * Reports the "creating" mode form's current client/brand/salesperson
   * selection as a composed preview-query input, or null while incomplete.
   * The caller feeds this into `useCampaignNumberPreview` and passes the
   * result back via `campaignNumberPreview` above — a controlled round trip
   * (mirrors `selectedDraftId`/`onSelectDraft`) made necessary by this
   * component owning the RHF form that drives the live values.
   */
  onCampaignNumberPreviewInputChange?: (input: CampaignNumberPreviewQuery | null) => void;
  /**
   * Sales-department user list for the "creating" mode salesperson picker,
   * and the caller's own id -- passed as plain props (not fetched via
   * `useOfferSalesUsers`/`useRouteContext` inside this component) so this
   * component keeps rendering with no router/query context, per
   * `apps/mockups`' "any real apps/web component should render with no
   * code changes" contract (see `building-ui` skill, MOCKUPS.md).
   */
  salesUsers: SalesUser[];
  authUserId?: number | null;
  /**
   * Whether the caller holds offer.assign_other_salesperson — passed as a
   * plain prop for the same apps/mockups no-router-context reason as
   * salesUsers/authUserId above. Defaults to true (fail-open) when omitted;
   * only matters for sales-department callers, who otherwise get the
   * salesperson field locked to their own self-defaulted value.
   */
  canAssignOtherSalesperson?: boolean;
  /** Whether the current user has the client.create permission. Defaults to false (hidden/disabled trigger) when omitted. */
  canCreateClient?: boolean;
  createDraftMode: 'inline' | 'chain';
  draftUnitLabel: string;
  draftSummarySeparator: string;
  /** Locked-target summary: prefixes the pending unit count, e.g. "Dodajesz: 2 nośników". */
  pendingUnitsLabel: string;
  targetOfferLabel: string;
  searchPlaceholder: string;
  createNewLinkLabel: string;
  changeSelectionLabel: string;
  cancelChangeLabel: string;
  backToExistingLabel: string;
  lastUsedHintLabel: string;
  destinationLabel: string;
  creationStatusHintLabel: string;
  noPreselectionLabel: string;
  noDraftsLabel: string;
  noPermissionLabel: string;
  /** Shown instead of the draft list when the search query matches no editable draft. */
  noResultsLabel: string;
  createNewLabel: string;
  createNewHint: string;
  clientFieldLabel: string;
  clientFieldPlaceholder: string;
  salespersonFieldLabel: string;
  salespersonFieldPlaceholder: string;
  salespersonFieldLockedHint: string;
  confirmExistingLabel: string;
  confirmNewLabel: string;
  cancelLabel: string;
  openFullFormLabel: string;
  openFullFormHint: string;
  validationClientRequiredLabel: string;
  inlineClientValue?: string;
  staticPreview?: boolean;
  /**
   * Overrides the computed default mode (see `getDefaultMode`) — e.g. force `'changing'`
   * when this instance is opened via an external "Zmień"/pick-offer trigger that already
   * has a valid `selectedDraftId`, which would otherwise default straight back to
   * `'summary'` and require an extra click before the search/list is reachable.
   */
  initialMode?: QuickAddOfferPickerMode;
  /**
   * True when this picker instance was opened from the availability
   * calendar's Unified SELECT popover (Rezerwacja/Opcja flow). Suppresses
   * the automatic navigate-to-new-offer step after a successful
   * `creating`-mode commit (CU-86cbc2vj1) — the caller stays in place so
   * the in-progress reservation/option flow isn't interrupted. That flow's
   * `onConfirm` (`handleCalendarPickerConfirm`) never uses the shared
   * quick-add commit pipeline, so `onNavigateToNewOffer` is typically left
   * unset for these instances anyway; this flag is the explicit guard.
   */
  isCalendarSelectionContext?: boolean;
  onConfirm?: (
    args: QuickAddOfferPickerConfirmArgs,
  ) => Promise<QuickAddOfferPickerConfirmResult> | QuickAddOfferPickerConfirmResult;
  onSuccess?: (result: QuickAddOfferPickerConfirmSuccessResult) => void;
  /**
   * Called after a successful non-calendar `creating`-mode commit, downstream
   * of the co-creators `SharingDialog` deferral decision (CU-86cbc2vj1) — the
   * caller navigates to the new offer's workspace. Not called for
   * existing-offer commits (`summary`/`changing`/locked-target) or when
   * `isCalendarSelectionContext` is set.
   */
  onNavigateToNewOffer?: (offerId: string) => void;
  onCancel?: () => void;
  onSelectDraft?: (draftId: string | null) => void;
  onOpenFullForm?: (args: QuickAddOfferPickerOpenFullFormArgs) => void;
}

export type QuickAddOfferPickerMode = 'summary' | 'changing' | 'creating';

/**
 * Carries the picker's current state to the caller's "Otwórz pełny formularz"
 * navigation handler (CU-86cb8grgn) — the media units the picker was opened
 * with, plus whatever the caller already typed into the `creating`-mode inline
 * form (client, draft name, salesperson, campaign duration) so none of it is
 * silently lost when leaving the picker for the full `/offers/new` form.
 */
export interface QuickAddOfferPickerOpenFullFormArgs {
  mediaUnitIds: Array<string | number>;
  clientId?: string | null;
  salespersonId?: number | null;
  brand?: string | null;
  defaultDateFrom?: Date | null;
  defaultDateTo?: Date | null;
}

export interface QuickAddOfferPickerConfirmArgs {
  mode: QuickAddOfferPickerMode;
  offerId: string | null;
  offerName?: string | null;
  mediaUnitIds: Array<string | number>;
  clientId?: string | null;
  salespersonId?: number | null;
  brand?: string | null;
  /** Campaign duration for a newly created offer. Only set in `mode: 'creating'`. */
  defaultDateFrom?: Date | null;
  defaultDateTo?: Date | null;
  /**
   * Set when the picker's destination offer was pinned by a `lockedTarget`
   * (offer-context caller) rather than chosen by the user from their draft
   * list. Confirm handlers use this to skip the "remember last-used draft"
   * preference write, which only applies to user-chosen drafts.
   */
  isLockedTarget?: boolean;
}

export interface QuickAddOfferPickerConfirmSuccessResult {
  status: 'success';
  offerId: string;
}

export interface QuickAddOfferPickerConfirmRecoverForbiddenResult {
  status: 'recover-forbidden';
}

/**
 * Distinct from `recover-forbidden` (losing edit access to an *existing*
 * offer, where falling back to the draft list is the right recovery step):
 * this covers a 403 while *creating* the offer itself (`mode: 'creating'`,
 * e.g. a sales-department caller lacking `offer.assign_other_salesperson`).
 * No offer exists yet, so there is nothing to recover to — the picker stays
 * in `creating` mode with the caller's already-typed values intact instead
 * of resetting to the draft list (offer-client-collaboration, CU-86cbc2qv8).
 */
export interface QuickAddOfferPickerConfirmCreationFailedResult {
  status: 'creation-failed';
}

export type QuickAddOfferPickerConfirmResult =
  | QuickAddOfferPickerConfirmSuccessResult
  | QuickAddOfferPickerConfirmRecoverForbiddenResult
  | QuickAddOfferPickerConfirmCreationFailedResult;

export function QuickAddOfferPicker(rawProps: QuickAddOfferPickerProps) {
  const props = deriveEffectiveProps(rawProps);
  const salesUserOptions = React.useMemo(
    () => toSalesUserOptions(props.salesUsers),
    [props.salesUsers],
  );
  const { mode, setMode, selectedDraftId, handleSelectDraft, clearSelectedDraft } =
    useQuickAddOfferPickerState(props);
  const {
    form: createDraftForm,
    isConfirmDisabled: createDraftConfirmDisabled,
    salespersonFieldDisabled,
    clientOptions: mergedClientOptions,
    selectedClientBrands,
    onClientCreated,
    campaignNumberPreviewInput,
  } = useQuickAddCreateDraftForm(props);
  const selectedDraft = props.drafts.find((draft) => draft.id === selectedDraftId) ?? null;

  // Reports the "creating" mode form's live selection upward so the caller
  // can feed it into useCampaignNumberPreview and pass the result back via
  // campaignNumberPreview — this component never calls that hook itself
  // (offer-draft-numbering / "Campaign-number preview on offer creation
  // forms"; see QuickAddOfferPickerProps.campaignNumberPreview doc comment).
  const onCampaignNumberPreviewInputChange = props.onCampaignNumberPreviewInputChange;
  React.useEffect(() => {
    onCampaignNumberPreviewInputChange?.(campaignNumberPreviewInput);
  }, [campaignNumberPreviewInput, onCampaignNumberPreviewInputChange]);

  const handleConfirmResult = React.useCallback(
    (result: QuickAddOfferPickerConfirmResult | undefined, wasCreatingMode: boolean) => {
      if (!result) return;

      if (result.status === 'creation-failed') {
        // No offer was created (403 during the create step itself, e.g. a
        // sales-department caller lacking offer.assign_other_salesperson) —
        // stay in 'creating' mode with the caller's typed values intact
        // instead of resetting to the draft list. The pipeline already
        // showed a dedicated error toast for this case.
        return;
      }

      if (result.status === 'recover-forbidden') {
        // Locked target: there is no draft list to fall back into — the
        // destination cannot be changed from within the picker (see
        // `getDefaultMode`), so close instead of entering 'changing' mode.
        if (props.lockedTarget != null) {
          props.onCancel?.();
          return;
        }
        clearSelectedDraft();
        setMode('changing');
        return;
      }

      // Success. Auto-navigate to the new offer's workspace for a
      // non-calendar `creating`-mode commit (CU-86cbc2vj1) — downstream of
      // the co-creators `SharingDialog` deferral (see the two callers
      // below), so a checked "Dodaj współtwórców" intercepts this until the
      // dialog closes.
      if (wasCreatingMode && !props.isCalendarSelectionContext) {
        props.onNavigateToNewOffer?.(result.offerId);
      }

      props.onSuccess?.(result);
    },
    [clearSelectedDraft, props, setMode],
  );

  /** `handleConfirmResult` bound to `wasCreatingMode: true` for the two `creating`-mode call sites below. */
  const handleCreateModeConfirmResult = React.useCallback(
    (result: QuickAddOfferPickerConfirmResult | undefined) => handleConfirmResult(result, true),
    [handleConfirmResult],
  );

  const { addCoCreators, setAddCoCreators, maybeDeferToSharingDialog, sharingDialog } =
    useQuickAddCoCreators(handleCreateModeConfirmResult);

  const handleExistingConfirm = React.useCallback(async () => {
    if (selectedDraftId == null) return;

    const result = await props.onConfirm?.({
      mode,
      offerId: selectedDraftId,
      offerName: selectedDraft?.title ?? selectedDraftId,
      mediaUnitIds: props.mediaUnitIds ?? [],
      ...(props.lockedTarget != null ? { isLockedTarget: true } : {}),
    });

    handleConfirmResult(result, false);
  }, [handleConfirmResult, mode, props, selectedDraft, selectedDraftId]);

  const handleCreateDraftSubmit = createDraftForm.handleSubmit(async (values) => {
    const result = await props.onConfirm?.({
      mode: 'creating',
      offerId: null,
      mediaUnitIds: props.mediaUnitIds ?? [],
      clientId: values.clientId,
      ...(values.salespersonId != null ? { salespersonId: values.salespersonId } : {}),
      ...(values.brand ? { brand: values.brand } : {}),
      ...(values.defaultDateFrom ? { defaultDateFrom: values.defaultDateFrom } : {}),
      ...(values.defaultDateTo ? { defaultDateTo: values.defaultDateTo } : {}),
    });

    if (maybeDeferToSharingDialog(result)) return;

    handleCreateModeConfirmResult(result);
  });

  // Reads the inline "creating"-mode form's CURRENT values (not just its
  // initial defaults) so anything the caller already typed before clicking
  // "Otwórz pełny formularz" — client, salesperson, campaign duration —
  // travels with them to the full form instead of being silently discarded
  // (CU-86cb8grgn). Stays a plain `() => void` for the internal
  // Panel/Body/CreateNewSection/Button chain; only the caller-facing
  // `props.onOpenFullForm` receives the richer args object.
  const handleOpenFullForm = React.useCallback(() => {
    const values = createDraftForm.getValues();
    props.onOpenFullForm?.({
      mediaUnitIds: props.mediaUnitIds ?? [],
      clientId: values.clientId || null,
      salespersonId: values.salespersonId ?? null,
      brand: values.brand || null,
      defaultDateFrom: values.defaultDateFrom ?? null,
      defaultDateTo: values.defaultDateTo ?? null,
    });
  }, [createDraftForm, props]);

  const panel = (
    <QuickAddOfferPickerPanel
      {...props}
      onOpenFullForm={handleOpenFullForm}
      selectedDraftId={selectedDraftId}
      mode={mode}
      createDraftForm={createDraftForm}
      formId={CREATE_FORM_ID}
      createDraftConfirmDisabled={createDraftConfirmDisabled}
      salespersonFieldDisabled={salespersonFieldDisabled}
      salesUserOptions={salesUserOptions}
      clientOptions={mergedClientOptions}
      selectedClientBrands={selectedClientBrands}
      onClientCreated={onClientCreated}
      addCoCreators={addCoCreators}
      onAddCoCreatorsChange={setAddCoCreators}
      onCreateDraftSubmit={() => void handleCreateDraftSubmit()}
      onExistingConfirm={() => void handleExistingConfirm()}
      onModeChange={setMode}
      onSelectDraft={handleSelectDraft}
      showFooter={props.presentation !== 'modal' || props.staticPreview}
      showHeader={props.presentation !== 'modal' || props.staticPreview}
    />
  );

  const mainContent =
    props.presentation !== 'modal' || props.staticPreview ? (
      panel
    ) : (
      <QuickAddOfferPickerModal
        props={props}
        mode={mode}
        selectedDraftId={selectedDraftId}
        createDraftForm={createDraftForm}
        createDraftConfirmDisabled={createDraftConfirmDisabled}
        salespersonFieldDisabled={salespersonFieldDisabled}
        createDraftSubmit={() => void handleCreateDraftSubmit()}
        formId={CREATE_FORM_ID}
        salesUserOptions={salesUserOptions}
        clientOptions={mergedClientOptions}
        selectedClientBrands={selectedClientBrands}
        onClientCreated={onClientCreated}
        addCoCreators={addCoCreators}
        onAddCoCreatorsChange={setAddCoCreators}
        onExistingConfirm={() => void handleExistingConfirm()}
        onModeChange={setMode}
        onSelectDraft={handleSelectDraft}
        onOpenFullForm={handleOpenFullForm}
      />
    );

  return (
    <>
      {mainContent}
      {sharingDialog}
    </>
  );
}
